// Rich wakes (Node Control): from firmware .56 a node can take a short burst
// of full radar captures on a normal wake and upload them with that wake's
// data, so a node on battery or cellular gives rich radar data without live
// mode. The burst settings, what bursts cost, the node's upload cadence, the
// statistics of a burst's spectra (mean, spread, flicker) and the burst
// history, as served by devices/<mac>/radar/bursts/. Pure helpers: no React,
// network or storage.
import { duration } from "utils/fieldFormat";
import { burstSettingsText, finite, timeOf } from "utils/radarDiagnostics";
import {
  ASSUMED_STEP_MM,
  NEAR_FIELD_BINS,
  analyzeSpectrum,
  beatSpectrum,
  clipping,
  comparisonRows,
  pickDistance,
  quantile,
  spectrumReachMm,
  validPeakBin,
} from "utils/radarSignal";
import {
  STREAM_ADC,
  STREAM_DUMP,
  STREAM_ECHOES,
  STREAM_SPECTRUM,
  hasSpectrum,
  middleHalfMean,
  nodeSlug,
  partsSent,
  quantizeDb,
  readMask,
  readRawMode,
  utcStamp,
} from "utils/radarStream";

// radar_burst has radar_stream's bits: 1 spectrum, 2 beat signal, 4 echo
// list, 8 the radar's settings dump (once each time it is turned on); 0 off.
export const BURST_SPECTRUM = STREAM_SPECTRUM;
export const BURST_ADC = STREAM_ADC;
export const BURST_ECHOES = STREAM_ECHOES;
export const BURST_DUMP = STREAM_DUMP;
export const BURST_KEYS = [
  "radar_burst",
  "radar_burst_n",
  "radar_burst_gap_s",
  "radar_burst_every",
];
// Firmware .57: a burst only while the solar current is at least this many
// mA (0: whatever the sun). Sent with the others where the server accepts it.
export const BURST_SUN_KEY = "radar_burst_min_si";
// The firmware's limits, for a server that sends no rules.
export const BURST_RULES = {
  radar_burst: [0, 15],
  radar_burst_n: [1, 20],
  radar_burst_gap_s: [0, 60],
  radar_burst_every: [1, 96],
  radar_burst_min_si: [0, 500],
};
export const BURST_DEFAULTS = {
  mask: BURST_SPECTRUM,
  n: 5,
  gap: 0,
  every: 1,
  minSi: 0,
};
export const BURST_PAGE = 50; // the bursts endpoint's largest page
export const BURST_MAX_PAGES = 30; // at most 1,500 bursts per load
export const DAY_MS = 86400000;
export const BURST_WINDOWS = [
  [1440, "24 h"],
  [10080, "7 d"],
  [43200, "30 d"],
];

const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);

// The server's [low, high] for a key, else the firmware's.
export const ruleOf = (rules, key) =>
  Array.isArray(rules?.[key]) && rules[key].length === 2
    ? rules[key]
    : BURST_RULES[key];

// The values a key's rule allows when it is a set rather than a [low, high]
// range: the settings endpoint names those keys in `set_keys` (radar_baud's
// rates, the camera frame sizes); without that list, a rule of other than
// two entries is a set. -> the values, or null (a range, or no rule).
export function ruleSet(rules, key, setKeys = null) {
  const rule = rules?.[key];
  if (!Array.isArray(rule)) return null;
  const named = Array.isArray(setKeys) && setKeys.includes(key);
  return named || rule.length !== 2 ? rule : null;
}

// A whole number within [lo, hi] as read back, else null (not reported).
export function readWhole(value, [lo, hi]) {
  const v = finite(value);
  return v !== null && Number.isInteger(v) && v >= lo && v <= hi ? v : null;
}

// The burst settings as the node reports them; null where not reported.
export function readBurst(reported, rules) {
  return {
    mask: readMask(reported?.radar_burst),
    n: readWhole(reported?.radar_burst_n, ruleOf(rules, "radar_burst_n")),
    gap: readWhole(
      reported?.radar_burst_gap_s,
      ruleOf(rules, "radar_burst_gap_s"),
    ),
    every: readWhole(
      reported?.radar_burst_every,
      ruleOf(rules, "radar_burst_every"),
    ),
    minSi: readWhole(reported?.[BURST_SUN_KEY], ruleOf(rules, BURST_SUN_KEY)),
  };
}

// "spectrum, 5 captures 2 s apart, every 4th upload, only with at least
// 50 mA of solar"; "off" for 0.
export const burstText = (settings) => burstSettingsText(settings);

// What one burst capture message costs over the radio, as node 1's firmware
// .56 sends them (radar firmware 20260424 at 9600 baud): 2,057 B with the
// spectrum (mode 1) and 3,479 B with the beat signal (mode 2: its two ADC
// packets of each window). The echo list adds up to about 0.4 kB and a
// threshold block (from a raw output mode that sends one) about 1.4 kB, as in
// the stream.
export const BURST_BYTES = {
  base: 668,
  spectrum: 1389,
  adc: 2811,
  threshold: 1371,
  echoes: 400,
};
// How long the radar takes for one capture at 9600 baud: about 4.3 s for a
// spectrum, as measured on node 1; another packet of about 1 kB (the beat
// signal, a threshold block) adds about 1.1 s on the serial line, and an
// echo-list query alone about half a second.
export const CAPTURE_SECONDS = 4.3;
export const PACKET_SECONDS = 1.1;
export const ECHO_QUERY_SECONDS = 0.5;

export function burstCaptureBytes(mask, { threshold = false } = {}) {
  const m = readMask(mask) ?? 0;
  if (!(m & (BURST_SPECTRUM | BURST_ADC | BURST_ECHOES))) return 0;
  let bytes = BURST_BYTES.base;
  if (m & BURST_SPECTRUM)
    bytes += BURST_BYTES.spectrum + (threshold ? BURST_BYTES.threshold : 0);
  if (m & BURST_ADC) bytes += BURST_BYTES.adc;
  if (m & BURST_ECHOES) bytes += BURST_BYTES.echoes;
  return bytes;
}

export function burstCaptureSeconds(mask, { threshold = false } = {}) {
  const m = readMask(mask) ?? 0;
  if (!(m & (BURST_SPECTRUM | BURST_ADC)))
    return m & BURST_ECHOES ? ECHO_QUERY_SECONDS : 0;
  let seconds = CAPTURE_SECONDS;
  if (m & BURST_SPECTRUM && m & BURST_ADC) seconds += PACKET_SECONDS;
  if (m & BURST_SPECTRUM && threshold) seconds += PACKET_SECONDS;
  return seconds;
}

// What bursts cost at a cadence: bytes per capture and per burst, bursts a
// day (one on every `every`-th upload, uploads `uploadSeconds` apart), data a
// day and a 30-day month, and how long the radar runs per burst and per day
// (the captures and the pauses between them). The radar sends one kind of
// packet per raw output mode, so a capture holds only the part ticked that
// its mode sends (`parts`); alternating (`alt`, radar_raw_alt) captures take
// turns in modes 1 and 2, so a capture costs the mean of the two. The
// settings dump goes once, so it is not counted.
export function burstEstimate({
  mask,
  mode = null,
  alt = false,
  n,
  gap = 0,
  every = 1,
  uploadSeconds,
  threshold = false,
}) {
  const turns = (alt ? ["spectrum", "beat"] : [null]).map((turn) => {
    const parts = partsSent(mask, mode, turn);
    return {
      parts,
      bytes: burstCaptureBytes(parts, { threshold }),
      seconds: burstCaptureSeconds(parts, { threshold }),
    };
  });
  const mean = (key) =>
    turns.reduce((sum, t) => sum + t[key], 0) / turns.length;
  const parts = turns.reduce((all, t) => all | t.parts, 0);
  const capture = mean("bytes");
  const count = Math.max(1, Math.round(finite(n) ?? BURST_DEFAULTS.n));
  const pause = Math.max(0, finite(gap) ?? 0);
  const nth = Math.max(1, Math.round(finite(every) ?? 1));
  const upload = finite(uploadSeconds);
  const perDay =
    upload && upload > 0 && capture > 0 ? 86400 / (upload * nth) : 0;
  const burst = capture * count;
  const captureSeconds = mean("seconds");
  const radarSeconds =
    captureSeconds > 0 ? count * captureSeconds + (count - 1) * pause : 0;
  return {
    parts,
    capture,
    count,
    burst,
    perDay,
    day: burst * perDay,
    month: burst * perDay * 30,
    captureSeconds,
    radarSeconds,
    radarDay: radarSeconds * perDay,
    everySeconds: upload && upload > 0 ? upload * nth : null,
  };
}

// Live mode (firmware rtct.33 on): on external power the node stays awake and
// sends a sample every live_s seconds, each with live: true, live_seq (its
// place in the session, from 1) and live_s. A live session starts after a
// normal wake's upload and takes no burst: bursts come on normal wakes only.
export const isLiveReading = (s) =>
  s?.raw?.live === true || finite(s?.raw?.live_seq) !== null;
// The node's live sessions in its readings, as {from, to} in ms (the node's
// clock), oldest first. A live reading lies in a session that began
// (live_seq - 1) x live_s before it, so the oldest reading at hand still
// dates the start of its session, but never before the normal reading that
// precedes it (the wake that started it). Live readings join one session
// unless a normal reading falls between them, or the later one dates its
// session more than two samples (at least 2 min) after the earlier one. A
// session whose newest reading is the node's newest, and recent (as the
// server judges a running session: within 3 x live_s + 30 s, at least 90 s),
// runs on to `now`.
export const LIVE_JOIN_MS = 120000;
export function liveSpans(samples, { now = Date.now() } = {}) {
  const readings = (samples || [])
    .map((s) => ({
      t: timeOf(s?.timestamp) ?? timeOf(s?.received_at),
      live: isLiveReading(s),
      seq: finite(s?.raw?.live_seq),
      every: finite(s?.raw?.live_s),
    }))
    .filter((r) => r.t !== null)
    .sort((a, b) => a.t - b.t);
  const spans = [];
  let wake = -Infinity;
  for (const r of readings) {
    if (!r.live) {
      wake = r.t;
      continue;
    }
    const back =
      r.seq !== null && r.seq >= 1 && r.every !== null && r.every > 0
        ? (r.seq - 1) * r.every * 1000
        : 0;
    const from = Math.max(r.t - back, wake);
    const span = spans[spans.length - 1];
    const join = Math.max(LIVE_JOIN_MS, 2 * (r.every ?? 0) * 1000);
    if (span && wake < span.to && from <= span.to + join) {
      span.from = Math.min(span.from, from);
      span.to = Math.max(span.to, r.t);
      span.every = r.every ?? span.every;
    } else spans.push({ from, to: r.t, every: r.every });
  }
  const newest = readings[readings.length - 1];
  const open = spans[spans.length - 1];
  if (open && newest?.live && newest.t === open.to) {
    const fresh = Math.max(90, 3 * (open.every ?? 0) + 30) * 1000;
    if (now - open.to <= fresh) open.to = Math.max(open.to, now);
  }
  return spans.map(({ from, to }) => ({ from, to }));
}
// How long the spans cover of [from, to], in ms.
export function liveWithin(spans, from, to) {
  return (spans || []).reduce(
    (sum, s) => sum + Math.max(0, Math.min(s.to, to) - Math.max(s.from, from)),
    0,
  );
}

// How often the node uploads. From its settings: it wakes every interval_sec
// (lte_interval_s on cellular) and uploads on every wake or, with
// send_every_s (lte_send_every_s) above 0, on the first wake at least that
// long after its last upload. As observed, preferred once its readings hold
// five uploads outside live mode: readings that arrived within a minute of
// each other came in one upload, and the spacing is the mean of the middle
// half of the gaps between its last 12, leaving out a gap that spans a live
// session (no normal wake runs then); it needs three such gaps. Live-mode
// samples are no uploads: bursts come on normal wakes only. `uploads` counts
// the uploads outside live mode and `live` the live readings at hand.
export const UPLOAD_GROUP_MS = 60000;
export const OBSERVE_UPLOADS = 5;
export const OBSERVE_GAPS = 3;
export function uploadCadence(
  reported,
  samples,
  { cellular = false, spans = null } = {},
) {
  const wakeValue = finite(
    reported?.[cellular ? "lte_interval_s" : "interval_sec"],
  );
  const wake =
    wakeValue !== null && wakeValue > 0 ? wakeValue : cellular ? 3600 : 300;
  const sendValue = finite(
    reported?.[cellular ? "lte_send_every_s" : "send_every_s"],
  );
  const send = sendValue !== null && sendValue > 0 ? sendValue : 0;
  const settings = send > 0 ? Math.ceil(send / wake) * wake : wake;
  const live = spans ?? liveSpans(samples);
  const normal = (samples || [])
    .filter((s) => !isLiveReading(s))
    .map((s) => {
      const taken = timeOf(s?.timestamp);
      const at = timeOf(s?.received_at) ?? taken;
      return { at, taken: taken ?? at };
    })
    .filter((r) => r.at !== null)
    .sort((a, b) => a.at - b.at);
  // Uploads: when each arrived, and the first and last wake it carried.
  const uploads = [];
  let last = -Infinity;
  for (const r of normal) {
    const u = uploads[uploads.length - 1];
    if (r.at - last > UPLOAD_GROUP_MS)
      uploads.push({ at: r.at, first: r.taken, last: r.taken });
    else {
      u.first = Math.min(u.first, r.taken);
      u.last = Math.max(u.last, r.taken);
    }
    last = r.at;
  }
  const recent = uploads.slice(-12);
  const gaps = [];
  for (let i = 1; i < recent.length; i++) {
    const [a, b] = [recent[i - 1], recent[i]];
    if (b.at > a.at && !live.some((s) => s.from < b.first && s.to > a.last))
      gaps.push(b.at - a.at);
  }
  const spacing =
    uploads.length >= OBSERVE_UPLOADS && gaps.length >= OBSERVE_GAPS
      ? middleHalfMean(gaps)
      : null;
  const observed = spacing === null ? null : spacing / 1000;
  return {
    seconds: observed ?? settings,
    source: observed !== null ? "observed" : "settings",
    observed,
    settings,
    wake,
    send,
    cellular,
    uploads: uploads.length,
    live: (samples || []).filter(isLiveReading).length,
  };
}

// The upload cadence in words, with where it comes from: "every 2 min on
// Wi-Fi (as observed over 9 uploads outside live mode; its settings say
// 60 s)", or from its settings when its readings do not show it, saying why
// when live samples crowd the normal uploads out of them or live sessions
// split them.
export function cadenceText(cadence) {
  const c = cadence;
  const uploads = (n) => `${n.toLocaleString()} upload${n === 1 ? "" : "s"}`;
  const differs =
    c.observed !== null &&
    Math.abs(c.observed - c.settings) > 0.25 * c.settings;
  const source =
    c.source === "observed"
      ? `as observed${c.live ? ` over ${uploads(Math.min(c.uploads, 12))} outside live mode` : ""}${differs ? `; its settings say ${duration(c.settings)}` : ""}`
      : `from its settings${
          !c.live
            ? ""
            : !c.uploads
              ? ": its newest readings are all live samples"
              : c.uploads < OBSERVE_UPLOADS
                ? `: its newest readings hold only ${uploads(c.uploads)} outside live mode`
                : `: live sessions split the ${uploads(c.uploads)} in its newest readings`
        }`;
  return `every ${duration(c.seconds)} on ${c.cellular ? "cellular" : "Wi-Fi"} (${source})`;
}

// Bursts to expect over [from, to] at `perDay` (burstEstimate()) outside the
// node's live sessions (liveSpans()): bursts pause while live mode runs.
// -> {count, liveMs, live (a session in the window), liveNow (one runs at
// `to`)}.
export function expectedBursts(perDay, spans, from, to) {
  const span = Math.max(0, to - from);
  const liveMs = Math.min(span, liveWithin(spans, from, to));
  const inWindow = (spans || []).filter((s) => s.to >= from && s.from <= to);
  return {
    count: span > 0 ? (finite(perDay) ?? 0) * ((span - liveMs) / DAY_MS) : 0,
    liveMs,
    live: inWindow.length > 0,
    liveNow: inWindow.some((s) => s.to >= to),
  };
}

// The node's power as a report shows it: battery and input voltages, external
// power (an input of 4.30 V or more: USB or a working panel) and whether the
// node skips bursts now - below 3.25 V on a readable cell (1.5 V or more),
// unless it is on external power. With `cell` (firmware .58, whose
// radar_rich_min_bv sets the floor for rich readings and bursts) a readable
// cell under the floor skips them whatever the input reads.
export const EXT_POWER_V = 4.3;
export const BATTERY_FLOOR_V = 3.25;
export function burstPower(
  sample,
  { extV = EXT_POWER_V, floorV = BATTERY_FLOOR_V, cell = false } = {},
) {
  if (!sample) return null;
  const first = (...values) =>
    values.map(finite).find((v) => v !== null) ?? null;
  const battery = first(
    sample.battery_voltage,
    sample.raw?.bat_v,
    sample.raw?.battery_voltage,
  );
  const input = first(
    sample.solar_voltage,
    sample.raw?.sv,
    sample.raw?.solar_voltage,
  );
  const readable = battery !== null && battery >= 1.5;
  const external = input !== null && input >= extV;
  const low = readable && battery < floorV;
  return {
    battery: readable ? battery : null,
    input,
    external,
    low,
    skips: cell ? low : low && !external,
    t: sample.timestamp ?? null,
    extV,
    floorV,
  };
}

// Per-bin statistics of a burst's spectra in dB, over the captures that have a
// value at each bin: mean, lowest, highest, standard deviation (population;
// null with fewer than two values) and how many values. The mean of the dB
// values is the geometric mean of the magnitudes. `n` counts the spectra.
export function burstStats(captures) {
  const rows = (captures || []).filter(hasSpectrum).map((c) => c.db);
  const bins = rows.reduce((top, r) => Math.max(top, r.length), 0);
  const mean = new Array(bins).fill(null);
  const min = new Array(bins).fill(null);
  const max = new Array(bins).fill(null);
  const std = new Array(bins).fill(null);
  const count = new Array(bins).fill(0);
  for (let b = 0; b < bins; b++) {
    let n = 0;
    let sum = 0;
    let lo = Infinity;
    let hi = -Infinity;
    for (const r of rows) {
      const v = num(r[b]);
      if (v === null) continue;
      n += 1;
      sum += v;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    count[b] = n;
    if (!n) continue;
    const m = sum / n;
    let squares = 0;
    for (const r of rows) {
      const v = num(r[b]);
      if (v !== null) squares += (v - m) ** 2;
    }
    mean[b] = m;
    min[b] = lo;
    max[b] = hi;
    std[b] = n >= 2 ? Math.sqrt(squares / n) : null;
  }
  return { n: rows.length, bins, mean, min, max, std, count };
}

// Flicker: how much the burst's spectra change from capture to capture, the
// per-bin standard deviation in dB. On node 1 a steady scene gives about
// 0.2 dB beyond the near field (the median) and 1-2 dB at most; insects or
// drops crossing the beam, or ripple on the water, flicker far more at their
// range. Bin 0 (DC, which the radar removes) and the rest of the near field
// are left out. The median says how steady the spectrum is overall, the peak
// where it changes most.
export function burstFlicker(
  stats,
  { step, cal, skip = NEAR_FIELD_BINS } = {},
) {
  const values = [];
  let peak = null;
  for (let b = skip; b < (stats?.std?.length || 0); b++) {
    const s = num(stats.std[b]);
    if (s === null) continue;
    values.push(s);
    if (!peak || s > peak.std) peak = { bin: b, std: s };
  }
  const st = finite(step) || ASSUMED_STEP_MM;
  const offset = finite(cal) ?? 0;
  return {
    median: quantile(values, 0.5),
    peak: peak && { ...peak, mm: peak.bin * st - offset },
    bins: values.length,
  };
}

// The radar's picks across a burst, the valid ones only (not 0, a status code
// or beyond the spectrum's reach): lowest, highest, median, how many, and the
// median of its valid peak bins.
export function burstPicks(captures) {
  const picks = [];
  const bins = [];
  for (const c of captures || []) {
    const size = Array.isArray(c?.db) ? c.db.length : 256;
    const p = pickDistance(
      c?.air_mm,
      spectrumReachMm(c?.step_mm, c?.cal_mm, size),
    );
    if (p !== null) picks.push(p);
    const b = validPeakBin(c?.peak_bin, size);
    if (b !== null) bins.push(b);
  }
  return {
    count: picks.length,
    min: picks.length ? Math.min(...picks) : null,
    max: picks.length ? Math.max(...picks) : null,
    median: quantile(picks, 0.5),
    peakBin: bins.length ? Math.round(quantile(bins, 0.5)) : null,
  };
}

// A burst's kind: "rich" for a rich reading (firmware .58: one small burst a
// wake), "burst" for a classic burst (radar_burst) and older data without a
// kind.
export const burstKind = (burst) =>
  String(burst?.kind ?? "")
    .trim()
    .toLowerCase() === "rich"
    ? "rich"
    : "burst";

const byIndex = (a, b) =>
  (finite(a?.i) ?? 0) - (finite(b?.i) ?? 0) ||
  (timeOf(a?.t) ?? 0) - (timeOf(b?.t) ?? 0);

// A burst's range step (mm between bins). A capture with a spectrum carries
// the radar's own (its RangeRes); a beat signal alone carries none, and the
// server gives it an assumed 30 mm, which put node 2's water 58 mm short (1,113
// mm for 1,171 mm at 31.5 mm a bin) once rich readings sent the beat signal
// first. So: a spectrum's step, else the server analysis' (its range_axis():
// the node's newest RangeRes), else any capture's.
export function burstStep(captures, analysis = null) {
  const spectrum = (captures || []).find(
    (c) => hasSpectrum(c) && finite(c?.step_mm),
  );
  return (
    finite(spectrum?.step_mm) ??
    finite(analysis?.step_mm) ??
    finite((captures || []).find((c) => finite(c?.step_mm))?.step_mm)
  );
}

// Everything the panel shows about one burst: its times, the captures
// expected and received, the per-bin statistics, the mean spectrum as a
// capture (with the radar's median pick, so analyzeSpectrum treats it like any
// capture), our analysis of it, the radar's picks, and flicker overall and at
// the chosen echo.
export function burstSummary(burst) {
  const captures = Array.isArray(burst?.captures)
    ? [...burst.captures].sort(byIndex)
    : [];
  const stats = burstStats(captures);
  const step = burstStep(captures, burst?.analysis);
  const cal =
    finite(captures.find((c) => finite(c?.cal_mm) !== null)?.cal_mm) ?? 0;
  const picks = burstPicks(captures);
  const mean = stats.n
    ? {
        id: null,
        t: burst?.t_first ?? null,
        db: stats.mean,
        thr_db: null,
        step_mm: step,
        cal_mm: cal,
        air_mm: picks.median,
        peak_bin: picks.peakBin,
        peaks: null,
      }
    : null;
  const analysis = mean ? analyzeSpectrum(mean) : null;
  const flicker = burstFlicker(stats, { step, cal });
  const echoBin = analysis?.chosen ? analysis.chosen.index : null;
  return {
    id: burst?.id ?? null,
    kind: burstKind(burst),
    t: burst?.t_first ?? null,
    ms: timeOf(burst?.t_first),
    lastMs: timeOf(burst?.t_last),
    expected: finite(burst?.n_expected),
    received: finite(burst?.n_received) ?? captures.length,
    mode: readRawMode(burst?.raw_mode),
    captures,
    stats,
    step,
    mean,
    analysis,
    // The server's estimate for the burst (estimator v2, analysis.our_mm), drawn
    // over the history beside the radar's pick.
    ours: finite(burst?.analysis?.our_mm),
    picks,
    flicker: {
      ...flicker,
      echo: echoBin === null ? null : num(stats.std[echoBin]),
    },
  };
}

// Chart rows for a burst between two distances (mm): each bin's reported
// distance, the mean, the band from the lowest to the highest value, the
// spread (std; none for bin 0, the DC bin the radar removes) and every
// capture's level as c0, c1, ... `keys` lists the captures that have a
// spectrum.
export function burstRows(summary, [lo, hi] = [-Infinity, Infinity]) {
  const stats = summary?.stats;
  const captures = summary?.captures || [];
  const st = finite(summary?.mean?.step_mm) || ASSUMED_STEP_MM;
  const offset = finite(summary?.mean?.cal_mm) ?? 0;
  const rows = [];
  for (let b = 0; b < (stats?.bins || 0); b++) {
    const d = b * st - offset;
    if (d < lo || d > hi) continue;
    const low = stats.min[b];
    const row = {
      d,
      mean: stats.mean[b],
      band: low === null ? null : [low, stats.max[b]],
      lo: low,
      hi: stats.max[b],
      std: b === 0 ? null : stats.std[b],
    };
    captures.forEach((c, k) => {
      row[`c${k}`] = Array.isArray(c?.db) ? num(c.db[b]) : null;
    });
    rows.push(row);
  }
  return {
    rows,
    keys: captures
      .map((c, k) => (hasSpectrum(c) ? `c${k}` : null))
      .filter(Boolean),
  };
}

// One capture beside its burst: our analysis of it, and how far it lies off
// the burst's mean (the RMS difference over the bins beyond the near field
// where both have a value), so a capture that flickers stands out.
export function captureVsMean(capture, stats, { skip = NEAR_FIELD_BINS } = {}) {
  const db = Array.isArray(capture?.db) ? capture.db : [];
  let squares = 0;
  let n = 0;
  for (let b = skip; b < db.length; b++) {
    const v = num(db[b]);
    const m = num(stats?.mean?.[b]);
    if (v === null || m === null) continue;
    squares += (v - m) ** 2;
    n += 1;
  }
  return {
    analysis: hasSpectrum(capture) ? analyzeSpectrum(capture) : null,
    offMean: n ? Math.sqrt(squares / n) : null,
  };
}

// One trend point per burst, from its mean spectrum: the SNR of the echo the
// radar chose, near-field energy over the floor (fouling), echoes 8 dB over
// the floor (ours), the radar's median pick and the burst's peak flicker.
export const burstTrend = (s) => ({
  ms: s.ms,
  snr: s.analysis?.chosen?.snr ?? null,
  near: s.analysis?.nearField?.overFloor ?? null,
  echoes: s.analysis ? s.analysis.echoes.count : null,
  pick: s.picks.median,
  flicker: s.flicker.peak?.std ?? null,
});

// The burst history as a waterfall, in decodeWaterfall's shape: one column
// per burst (oldest first), its mean spectrum on 0.5 dB codes, the radar's
// median pick, and the burst's id. A burst without a spectrum is a gap.
export function burstWaterfall(summaries, { unit = null, dbStep = 0.5 } = {}) {
  const list = (summaries || [])
    .filter((s) => s && s.ms !== null)
    .sort((a, b) => a.ms - b.ms);
  let low = Infinity;
  for (const s of list)
    for (const v of s.stats.mean) {
      const n = num(v);
      if (n !== null && n < low) low = n;
    }
  const dbMin = Number.isFinite(low)
    ? Math.floor(low / dbStep) * dbStep - dbStep
    : null;
  const ref = [...list].reverse().find((s) => s.mean);
  const bins = ref?.stats.bins || 256;
  const columns = list.map((s) => {
    const codes = s.mean
      ? quantizeDb(s.stats.mean, dbMin, dbStep, bins)
      : new Uint8Array(bins);
    return {
      t: s.t,
      ms: s.ms,
      air: s.picks.median,
      ours: s.ours ?? null,
      codes,
      empty: codes.every((c) => c === 0),
      burstId: s.id,
    };
  });
  return {
    columns,
    bins,
    step: finite(ref?.mean?.step_mm),
    cal: finite(ref?.mean?.cal_mm) ?? 0,
    dbMin,
    dbStep,
    dbUnit: unit,
    decimated: false,
    short: false,
  };
}

// The burst's beat signals beside the radar's mean spectrum: our FFT of each
// capture's samples (Hann, ×8 zero-padding; o0, o1, ... as thin lines), the
// mean of those (ours) and of our unwindowed, unpadded FFTs (rect, the
// radar's own way), each scaled to 0 dB at its strongest echo beyond the
// near field as comparisonRows does; the radar's mean spectrum as `radar`.
// `beats`: [{samples, clip}], clip as clipFrom() gives it (the server's, else
// ours). With `unclipped`, each Hann FFT covers only the samples after the
// capture's last clipped one (lower resolution).
export function burstBeatRows(
  beats,
  radarDb,
  step,
  cal,
  domain,
  { unclipped = false } = {},
) {
  const items = (beats || [])
    .map(({ samples, clip }) => ({
      clip: clip ?? clipping(samples),
      hann: beatSpectrum(samples, {
        pad: 8,
        from: unclipped && clip?.clipped ? clip.last + 1 : 0,
      }),
      rect: beatSpectrum(samples, { pad: 1, window: "rect" }),
    }))
    .filter((x) => x.hann && x.rect);
  if (!items.length)
    return { rows: [], ffts: [], mean: null, rect: null, clips: [] };
  const average = (list) => {
    const db = new Float64Array(list[0].db.length);
    for (let k = 0; k < db.length; k++) {
      let sum = 0;
      let n = 0;
      for (const f of list)
        if (Number.isFinite(f.db[k])) {
          sum += f.db[k];
          n += 1;
        }
      db[k] = n ? sum / n : -Infinity;
    }
    return { db, pad: list[0].pad, samples: list[0].samples };
  };
  const same = items.filter(
    (x) =>
      x.hann.db.length === items[0].hann.db.length &&
      x.rect.db.length === items[0].rect.db.length,
  );
  const ffts = same.map((x) => x.hann);
  const mean = average(ffts);
  const rect = average(same.map((x) => x.rect));
  const rows = comparisonRows(radarDb, mean, step, cal, domain, rect);
  ffts.forEach((f, j) => {
    comparisonRows(null, f, step, cal, domain).forEach((r, i) => {
      if (rows[i]) rows[i][`o${j}`] = r.ours;
    });
  });
  return { rows, ffts, mean, rect, clips: same.map((x) => x.clip) };
}

// A burst's clipping in %: the mean of its beat signals' shares of samples at
// a rail (0 for one that does not clip); null without any.
export function burstClipping(pcts) {
  const list = (pcts || []).map(finite).filter((v) => v !== null);
  return list.length ? list.reduce((a, b) => a + b, 0) / list.length : null;
}
// The same from a burst's capture items, which carry the server's clip_pct
// (null without a beat signal; undefined from a server before clipping).
export const capturesClipPct = (captures) =>
  burstClipping(
    (captures || []).filter((c) => c?.has_adc).map((c) => c.clip_pct),
  );

// ---- The bursts as the browser keeps them ----

export const burstKey = (b) => String(b?.id);
// A burst as the endpoint serves it, if usable: an id and a first time.
export const validBurst = (b) =>
  !!b && b.id !== null && b.id !== undefined && timeOf(b.t_first) !== null;
export const burstList = (data) =>
  Array.isArray(data?.bursts) ? data.bursts.filter(validBurst) : [];
const newestFirst = (a, b) =>
  timeOf(b.t_first) - timeOf(a.t_first) ||
  (finite(b.id) ?? 0) - (finite(a.id) ?? 0);
const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
const sameBurst = (a, b) =>
  a.t_last === b.t_last &&
  a.n_received === b.n_received &&
  (a.captures?.length ?? 0) === (b.captures?.length ?? 0) &&
  same(a.reading, b.reading) &&
  same(a.energy, b.energy) &&
  same(a.analysis, b.analysis);

// Bursts kept in the browser: one per id, newest first; a later copy of a
// burst (more captures arrived, its reading, its energy or its analysis)
// replaces the earlier one. Answers can cross (a poll for newer bursts and
// a re-ask for pending analyses): an earlier copy (an older t_last) never
// replaces a later one, and a copy still pending keeps the analysis already
// held. Returns `old` when nothing changed, so work memoised on the bursts
// is kept.
export function mergeBursts(old, incoming) {
  const byId = new Map((old || []).map((b) => [burstKey(b), b]));
  let changed = false;
  for (const b of incoming || []) {
    if (!validBurst(b)) continue;
    const prev = byId.get(burstKey(b));
    let next = b;
    if (prev) {
      const was = timeOf(prev.t_last);
      const now = timeOf(b.t_last);
      if (was !== null && now !== null && now < was) continue;
      if (now === was && prev.analysis && analysisPending(b))
        next = { ...b, analysis: prev.analysis };
      if (sameBurst(prev, next)) continue;
    }
    byId.set(burstKey(b), next);
    changed = true;
  }
  return changed ? [...byId.values()].sort(newestFirst) : old || [];
}

// The time to continue from after a page: its newest t_last (the bursts
// endpoint selects by t_last), else t_first.
const lastOf = (b) => timeOf(b.t_last) ?? timeOf(b.t_first);
const newestLast = (list) =>
  list.reduce((a, b) => (!a || lastOf(b) > lastOf(a) ? b : a), null);

// Where the next poll starts: after the newest t_last held. A burst still
// filling gets a later t_last with each capture, so it comes back by itself.
export function pollSince(bursts, fallback) {
  const newest = newestLast(bursts || []);
  return newest ? newest.t_last || newest.t_first : fallback;
}

// A burst whose analysis the server has not made yet: it lists `analysis`
// null until then (it analyses a few a request, the newest first); a server
// before analyses lists no `analysis` at all, which is not pending.
export const analysisPending = (b) =>
  !!b && Object.prototype.hasOwnProperty.call(b, "analysis") && !b.analysis;

// Where to ask again for the analyses still pending among the bursts held:
// just before the oldest of them (the endpoint selects by t_last), so one
// page brings it back with the bursts after it, and the server analyses the
// newest of those still missing. Null when none is pending.
export function pendingSince(bursts) {
  const times = (bursts || [])
    .filter(analysisPending)
    .map(lastOf)
    .filter(Number.isFinite);
  return times.length ? new Date(Math.min(...times) - 1).toISOString() : null;
}

// Pages through the bursts after `since` as the endpoint is meant to be
// paged: each page holds the first `limit` bursts whose t_last is after
// `since` (newest first), and while a page comes back full, ask again from its
// newest t_last. Stops at a short page, at a full one that brings nothing new,
// at bursts already `known` (when `stopAtKnown`, extending a loaded window
// back), or after `maxPages` (why: "cap"). `fetchPage(since, limit)` returns
// the endpoint's answer.
export async function pageBursts(
  fetchPage,
  since,
  { known = new Set(), stopAtKnown = false, maxPages = BURST_MAX_PAGES } = {},
) {
  const found = [];
  const seen = new Set(known);
  let cursor = since;
  let complete = false;
  let serverTime = null;
  let unit = null;
  for (let page = 0; page < maxPages && !complete; page++) {
    const data = await fetchPage(cursor, BURST_PAGE);
    if (timeOf(data?.server_time) !== null) serverTime = data.server_time;
    if (typeof data?.db_unit === "string") unit = data.db_unit;
    const list = burstList(data);
    let fresh = 0;
    let reached = false;
    for (const b of list) {
      if (known.has(burstKey(b))) reached = true;
      if (!seen.has(burstKey(b))) fresh += 1;
      seen.add(burstKey(b));
      found.push(b);
    }
    if (list.length < BURST_PAGE || (stopAtKnown && reached) || !fresh)
      complete = true;
    else cursor = pollSince(list, cursor);
  }
  return {
    bursts: found,
    complete,
    why: complete ? null : "cap",
    serverTime,
    unit,
  };
}

// Where to load from when the window grows further back. Rich readings bring
// a burst every wake (about 144 a day on a 10-minute wake), more than one
// load takes over 30 days, and paging runs forward in time: loading from the
// window's start would bring its oldest part and leave a gap before the
// bursts held. So a longer window loads back from the bursts held by about
// `budget` bursts at the density they show (bursts per ms over the span
// covered, to `now`), contiguous with them, and no further than `from`.
// Sparse nodes (classic bursts) load the whole window. A first load
// (`covered` null) of a window over two days long takes the last day, and the
// rest follows as an extension; a shorter one loads whole.
export const EXTEND_BUDGET = Math.round(BURST_PAGE * BURST_MAX_PAGES * 0.8);
export function loadStart(
  bursts,
  covered,
  from,
  { now = Date.now(), budget = EXTEND_BUDGET } = {},
) {
  if (covered === null || covered === undefined)
    return now - from > 2 * DAY_MS ? Math.max(from, now - DAY_MS) : from;
  const span = Math.max(3600000, now - covered);
  const held = (bursts || []).filter(
    (b) => (timeOf(b?.t_first) ?? -Infinity) >= covered,
  ).length;
  const perMs = held / span;
  return perMs > 0 ? Math.max(from, covered - budget / perMs) : from;
}

// ---- The wake's own account ----

// jjy_burst {id, n, ok, fail, ms, skip}: the burst the wake planned (n), the
// captures that worked and failed, how long it took and, when it was skipped
// or cut short, why.
export function burstAccount(value) {
  if (!value || typeof value !== "object") return null;
  const skip =
    value.skip === null || value.skip === undefined || value.skip === false
      ? ""
      : String(value.skip).trim();
  return {
    id: value.id ?? null,
    planned: finite(value.n),
    ok: finite(value.ok),
    fail: finite(value.fail),
    ms: finite(value.ms),
    skip: skip && skip !== "0" ? skip : null,
  };
}

// A skip reason in words, keeping the node's own token (firmware .56):
// "battery" -> "battery: the battery was below 3.25 V".
const SKIPS = {
  battery: "the battery was below 3.25 V, off external power",
  no_clock: "the node had no valid clock",
  radar: "the radar did not answer",
  no_memory: "the node was short of memory",
  live: "live mode was running",
  camera: "the wake was taking photos",
  time_cap: "the wake ran out of time",
  switched_off: "bursts were switched off after failed captures",
  sun: "the solar current was under the burst's threshold (radar_burst_min_si)",
};
export function skipText(skip) {
  const code = String(skip ?? "").trim();
  if (!code) return "";
  return SKIPS[code] ? `${code}: ${SKIPS[code]}` : code.replaceAll("_", " ");
}

// A tiny path of a spectrum between two distances (mm) for the burst list,
// scaled to its own range; bin 0 (DC) is left out and bins without a value
// break the line.
export function sparkline(
  db,
  { step, cal, from = 0, to = 2000, width = 96, height = 24 } = {},
) {
  const st = finite(step) || ASSUMED_STEP_MM;
  const offset = finite(cal) ?? 0;
  const points = [];
  for (let b = 1; b < (db?.length || 0); b++) {
    const d = b * st - offset;
    if (d >= from && d <= to) points.push([d, num(db[b])]);
  }
  const values = points.map((p) => p[1]).filter((v) => v !== null);
  if (values.length < 2) return "";
  const lo = Math.min(...values);
  const span = Math.max(...values) - lo || 1;
  const x0 = points[0][0];
  const xs = points[points.length - 1][0] - x0 || 1;
  let path = "";
  let pen = false;
  for (const [d, v] of points) {
    if (v === null) {
      pen = false;
      continue;
    }
    const x = ((d - x0) / xs) * width;
    const y = height - 1 - ((v - lo) / span) * (height - 2);
    path += `${pen ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`;
    pen = true;
  }
  return path;
}

// ---- Download ----

export const burstFileName = (mac, burst) =>
  `radar-burst-${nodeSlug(mac)}-${burst?.id ?? "unknown"}-${utcStamp(burst?.t_first)}.json`;

const round2 = (values) =>
  (values || []).map((v) =>
    num(v) === null ? null : Math.round(v * 100) / 100,
  );

// Everything known about one burst, for a file: the burst as the endpoint
// serves it, each capture as radar-captures/<id>/ serves it (or why it could
// not load), and our analysis.
export function burstExport({ mac, burst, summary, details, exportedAt }) {
  return {
    exported_at: exportedAt,
    mac,
    notes: [
      "Spectrum dB = 20·log10(bin value), assuming linear magnitude (unconfirmed).",
      "Distances = bin × step_mm − cal_mm, as the node reports them.",
      "analysis is the dashboard's own quick look, not the radar's: per-bin statistics over the burst's spectra (the mean of the dB values; population standard deviation = flicker), the mean analysed like a capture.",
    ],
    burst,
    capture_details: (burst?.captures || []).map((c) => {
      const d = details?.get?.(String(c?.id));
      return d?.data || { id: c?.id ?? null, error: d?.error || "not loaded" };
    }),
    analysis: summary
      ? {
          spectra: summary.stats.n,
          mean_db: round2(summary.stats.mean),
          min_db: round2(summary.stats.min),
          max_db: round2(summary.stats.max),
          std_db: round2(summary.stats.std),
          values_per_bin: summary.stats.count,
          picks: summary.picks,
          flicker: summary.flicker,
          mean: summary.analysis,
          captures: summary.captures.map((c) => {
            const { analysis, offMean } = captureVsMean(c, summary.stats);
            return {
              id: c.id ?? null,
              i: c.i ?? null,
              off_mean_db: offMean,
              analysis,
            };
          }),
        }
      : null,
  };
}
